import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Container,
  TextField,
  Button,
  Typography,
  Alert,
  Link,
  CircularProgress,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiService } from '../../services/api';

const schema = yup.object({
  new_password: yup
    .string()
    .min(8, 'Password must be at least 8 characters')
    .required('New password is required'),
  confirm_password: yup
    .string()
    .oneOf([yup.ref('new_password')], 'Passwords must match')
    .required('Please confirm your new password'),
});

interface ResetPasswordFormData {
  new_password: string;
  confirm_password: string;
}

export const ResetPasswordPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';

  const [error, setError] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetPasswordFormData>({
    resolver: yupResolver(schema),
  });

  const onSubmit = async (data: ResetPasswordFormData) => {
    try {
      setIsLoading(true);
      setError('');
      await apiService.resetPassword(token, data.new_password);
      setSuccess(true);
      setTimeout(() => navigate('/login', { replace: true }), 2000);
    } catch (err: any) {
      const apiError = apiService.handleApiError(err);
      setError(apiError.detail || 'Failed to reset password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}
      >
        <Card sx={{ maxWidth: 400, mx: 'auto', mt: 8 }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h4" component="h1" gutterBottom align="center">
              Set New Password
            </Typography>

            {!token && (
              <Alert severity="error" sx={{ mb: 2 }}>
                This link is missing its reset token. Ask a super admin for a
                fresh one.
              </Alert>
            )}

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {success ? (
              <Alert severity="success">
                Password updated. Redirecting to sign in…
              </Alert>
            ) : (
              <Box
                component="form"
                onSubmit={handleSubmit(onSubmit)}
                noValidate
              >
                <TextField
                  {...register('new_password')}
                  margin="normal"
                  required
                  fullWidth
                  label="New Password"
                  type="password"
                  autoComplete="new-password"
                  autoFocus
                  disabled={!token}
                  error={!!errors.new_password}
                  helperText={errors.new_password?.message}
                />
                <TextField
                  {...register('confirm_password')}
                  margin="normal"
                  required
                  fullWidth
                  label="Confirm New Password"
                  type="password"
                  autoComplete="new-password"
                  disabled={!token}
                  error={!!errors.confirm_password}
                  helperText={errors.confirm_password?.message}
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{ mt: 3, mb: 2 }}
                  disabled={isLoading || !token}
                >
                  {isLoading ? (
                    <CircularProgress size={24} />
                  ) : (
                    'Set New Password'
                  )}
                </Button>
                <Box textAlign="center">
                  <Link
                    component="button"
                    type="button"
                    variant="body2"
                    onClick={(e) => {
                      e.preventDefault();
                      navigate('/login');
                    }}
                  >
                    Back to Sign In
                  </Link>
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};
